import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FieldGroupModel } from 'src/shared/models/field/field-group';
import { FieldModel } from 'src/shared/models/field/field';
import { UserFieldResponse } from 'src/shared/models/field/user-field-response';
import { TabsProvider } from 'src/providers/tabs/tabs.provider';
import { SentryLogProvider } from 'src/providers/sentry/sentry.provider';
import { IProperty } from 'src/constants/global-interfaces';
import * as fp from 'lodash/fp';

export interface SetupableFormModel {
  antraegeName: string;
  fields: IProperty<any>[];
  defaultRadioValue?: string;
}

@Component({
  selector: 'rota-setupable-form',
  styleUrl: 'setupable-form.scss',
  templateUrl: 'setupable-form.html',
  standalone: false,
})
export class SetupableFormComponent implements OnInit, OnDestroy {
  @Input() fieldGroups$: Observable<Array<FieldGroupModel>>;

  @Input() requestModel: SetupableFormModel;

  form = this.fb.group({}); // Both used for rendering
  fieldGroups = new Array<FieldGroupModel>(); // Metadata for form

  private fields = new Array<FieldModel>();
  private fieldsSubscription: Subscription;
  private selectedOptions = new Array<string>();

  @Output() cancel = new EventEmitter<void>();
  @Output() customSubmit = new EventEmitter<Array<UserFieldResponse>>();

  get isEmpty() {
    return !this.fieldGroups.map((f) => (f.fields || []).length).reduce((acc, cur) => acc + cur, 0);
  }

  get dependentFieldsPerOptionId() {
    const dependentFieldsPerOptionId = this.fieldGroups
      .filter((fg) => fg.optionId)
      .reduce(
        (acc: any, cur: any) => ({
          ...acc,
          [cur.optionId!]: acc[cur.optionId!] || 0 + cur.fields.length,
        }),
        {}
      );
    return Object.keys(dependentFieldsPerOptionId).length ? dependentFieldsPerOptionId : null;
  }
  get visibleFieldGroups() {
    return this.fieldGroups.filter(
      (fg) => !fg.optionId || this.selectedOptions.some((so) => so === fg.optionId)
    );
  }

  constructor(
    private fb: FormBuilder,
    private tabsProvider: TabsProvider,
    private sentry: SentryLogProvider
  ) {}

  async ngOnInit(): Promise<void> {
    this.fieldsSubscription = this.fieldGroups$.subscribe((fgs) => {
      fgs = fgs || [];
      this.fields = fgs.map((f) => f.fields).reduce((acc, val) => acc.concat(val), []);

      const controls: any = {};
      const controlLog: any[] = [];
      (this.fields || []).forEach((field) => {
        let defaultValue: any = null;
        let validators: any = null;
        try {
          defaultValue = field.defaultValue;
          validators = field.validators;
        } catch (error) {
          this.sentry.reportFlowError('request-form', 'form:validators-threw', error, {
            fieldId: field.id,
            name: field.name,
            type: field.type,
            textFieldType: field.textFieldType,
          });
        }
        controls[field.id] = [defaultValue, validators];
        controlLog.push({
          id: field.id,
          name: field.name,
          type: field.type,
          textFieldType: field.textFieldType,
          defaultValue,
          hasValidators: !!validators,
        });
      });

      this.form = this.fb.group(controls);
      this.fieldGroups = fgs;

      if (this.isEmpty && fgs.length) {
        this.sentry.reportFlowIssue('request-form', 'form:built-empty', {
          hint: 'no fields to render, SEND button stays disabled',
          groupIds: fgs.map((g) => g.id),
        });
      }

      this.sentry.trace('form:built', {
        groupCount: fgs.length,
        groups: fgs.map((g) => ({
          id: g.id,
          optionId: g.optionId ?? null,
          fieldIds: (g.fields || []).map((f) => f.id),
        })),
        controls: controlLog,
        isEmpty: this.isEmpty,
        dependentFieldsPerOptionId: this.dependentFieldsPerOptionId,
      });

      if (!fp.get('antraegeName', this.requestModel)) return;

      const options = fp.get(
        '[0].fields[0].options',
        fgs.filter((x) => !x.optionId)
      );
      if (options) {
        const field = options.find((x: any) => x.name === this.requestModel.antraegeName);

        if (field) this.requestModel.defaultRadioValue = field.id;
      } else return;

      if (!fp.first(this.requestModel.fields)) return;

      for (let i = 0; i < this.fieldGroups.length; i++) {
        const fieldGroup = this.fieldGroups[i];

        for (let j = 0; j < fieldGroup.fields.length; j++) {
          const field = fieldGroup.fields[j];

          for (let k = 0; k < this.requestModel.fields.length; k++) {
            const storageField = this.requestModel.fields[k];

            if (storageField['id'] === field.id) {
              field.value = storageField['value'];
            }
          }
        }
      }
    });
  }

  onSubmit(e: Event) {
    const controlState = Object.entries(this.form.controls || {}).map(([id, c]: any) => ({
      id,
      value: c.value === undefined ? '<undefined>' : c.value,
      valid: c.valid,
      errors: c.errors ?? null,
    }));

    this.sentry.trace('form:submit:clicked', {
      formValid: this.form.valid,
      formStatus: this.form.status,
      selectedOptions: this.selectedOptions,
      visibleGroupIds: this.visibleFieldGroups.map((g) => g.id),
      controls: controlState,
    });

    if (!this.form.valid) {
      this.sentry.reportFlowIssue('request-form', 'form:submit:blocked-invalid', {
        formStatus: this.form.status,
        invalidControls: controlState.filter((c) => !c.valid),
        selectedOptions: this.selectedOptions,
      });
      return;
    }

    if (this.form.valid) {
      const visibleFields = this.visibleFieldGroups
        .map((f) => f.fields)
        .reduce((acc, val) => acc.concat(val), []);
      const res = UserFieldResponse.fromFormValues(this.form.value);
      const mapped = res
        .filter((resp) => visibleFields.some((vf) => vf.id === resp.fieldId))
        .map(
          (resp) =>
            new UserFieldResponse(resp.fieldId, resp.response, {
              field: this.fields.find((f) => f.id === resp.fieldId),
            })
        )
        .sort((a, b) => a.meta!.field!.order - b.meta!.field!.order);

      this.sentry.trace('form:submit:mapped', {
        rawFromForm: res.map((r: any) => ({ fieldId: r.fieldId, response: r.response })),
        visibleFieldIds: visibleFields.map((f) => f.id),
        droppedAsNotVisible: res
          .filter((r: any) => !visibleFields.some((vf) => vf.id === r.fieldId))
          .map((r: any) => ({ fieldId: r.fieldId, response: r.response })),
        emitted: mapped.map((m) => ({
          fieldId: m.fieldId,
          name: m.meta?.field?.name,
          type: m.meta?.field?.type,
          response: m.response,
        })),
      });

      if (!mapped.length) {
        this.sentry.reportFlowIssue('request-form', 'form:submit:nothing-to-emit', {
          formValue: this.form.value,
          visibleFieldIds: visibleFields.map((f) => f.id),
        });
      }

      this.customSubmit.emit(mapped);
    }
  }

  handleOptionSelect(selectedOptions: Array<string>) {
    this.selectedOptions = selectedOptions || [];
    this.sentry.trace('form:option-selected', {
      selectedOptions: this.selectedOptions,
      visibleGroupIds: this.visibleFieldGroups.map((g) => g.id),
    });
  }

  goBack() {
    this.tabsProvider.forwardBack();
  }

  ngOnDestroy() {
    if (this.fieldsSubscription) this.fieldsSubscription.unsubscribe();
  }
}
